import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import {
  getAllCostCenters,
  getCostCentersWithoutBalance,
  purchaseCredit,
  adjustBalance,
} from '../services/creditBalanceService';
import type {
  CostCenterOption,
  CreditTopUpFormValues,
} from '../types/creditBalance';

export type DialogMode =
  | { kind: 'purchase' }
  | { kind: 'topup'; costCenterId: string; costCenterName: string }
  | { kind: 'adjust'; costCenterId: string; costCenterName: string };

interface CreditPurchaseDialogProps {
  mode: DialogMode | null;
  onClose: () => void;
  onSuccess: () => void;
}

const i18nBase = 'resources.credit-consumption/credit-balance';

export const CreditPurchaseDialog: React.FC<CreditPurchaseDialogProps> = ({
  mode,
  onClose,
  onSuccess,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const [costCenters, setCostCenters] = useState<CostCenterOption[]>([]);
  const [loaded, setLoaded] = useState(false);

  const isTopUp = mode?.kind === 'topup';
  const isAdjust = mode?.kind === 'adjust';
  const isFixed = isTopUp || isAdjust; // cost center is pre-selected

  const { control, handleSubmit, reset, setValue } =
    useForm<CreditTopUpFormValues>({
      defaultValues: { costCenterId: '', amount: 0 },
      mode: 'all',
      reValidateMode: 'onChange',
      criteriaMode: 'all',
      shouldFocusError: true,
    });

  useEffect(() => {
    if (!mode) {
      setCostCenters([]);
      return;
    }
    let cancelled = false;
    setLoaded(false);
    const loader = isFixed ? getAllCostCenters : getCostCentersWithoutBalance;
    loader()
      .then((list) => {
        if (!cancelled) {
          setCostCenters(list);
          setLoaded(true);
        }
      })
      .catch((err) => {
        notify(
          extractErrorMessage(
            err,
            `${i18nBase}.notifications.loadCostCentersFailed`,
            translate
          ),
          { type: 'error' }
        );
      });
    return () => {
      cancelled = true;
    };
  }, [mode, isTopUp, notify, translate]);

  useEffect(() => {
    if (!mode) return;
    if (mode.kind === 'topup' || mode.kind === 'adjust') {
      reset({ costCenterId: mode.costCenterId, amount: 0 });
    } else {
      reset({ costCenterId: '', amount: 0 });
    }
  }, [mode, reset]);

  const onSubmit = async (values: CreditTopUpFormValues) => {
    setSubmitting(true);
    try {
      if (isAdjust) {
        await adjustBalance(values.costCenterId, Number(values.amount));
      } else {
        await purchaseCredit(values.costCenterId, Number(values.amount));
      }
      notify(`${i18nBase}.notifications.purchaseSucceeded`, {
        type: 'success',
      });
      onSuccess();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          `${i18nBase}.notifications.purchaseFailed`,
          translate
        ),
        { type: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if ((mode?.kind === 'topup' || mode?.kind === 'adjust') && costCenters.length === 0) {
      setCostCenters([{ id: mode.costCenterId, name: mode.costCenterName }]);
      setValue('costCenterId', mode.costCenterId);
    }
  }, [mode, costCenters.length, setValue]);

  return (
    <Dialog
      open={mode !== null}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {translate(
            isAdjust
              ? `${i18nBase}.dialog.adjustTitle`
              : isTopUp
                ? `${i18nBase}.dialog.topUpTitle`
                : `${i18nBase}.dialog.purchaseTitle`
          )}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          {!isFixed && loaded && costCenters.length === 0 ? (
            <Typography sx={{ py: 2, fontSize: 13, color: 'text.secondary' }}>
              {translate(`${i18nBase}.dialog.noEligibleCostCenters`)}
            </Typography>
          ) : (
          <Stack spacing={3}>
            <Controller
              name="costCenterId"
              control={control}
              rules={{
                required: translate(
                  `${i18nBase}.validation.costCenterRequired`
                ),
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  required
                  disabled={isFixed}
                  label={translate(`${i18nBase}.fields.costCenter`)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                >
                  {costCenters.map((cc) => (
                    <MenuItem key={cc.id} value={cc.id}>
                      {cc.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="amount"
              control={control}
              rules={{
                required: translate(
                  `${i18nBase}.validation.amountRequired`
                ),
                min: {
                  value: isAdjust ? 0 : 1,
                  message: translate(`${i18nBase}.validation.amountMin`),
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  required
                  label={translate(
                    isAdjust
                      ? `${i18nBase}.fields.newBalance`
                      : `${i18nBase}.fields.amount`
                  )}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: isAdjust ? 0 : 1 }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
              '& > .MuiButton-root': {
                height: 40,
                textTransform: 'none',
                width: { xs: '100%', sm: 120 },
                flex: { xs: '1 1 auto', sm: '0 0 120px' },
              },
            }}
          >
            <Button
              onClick={onClose}
              variant="contained"
              color="primary"
            >
              {translate('ra.action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || (!isFixed && loaded && costCenters.length === 0)}
            >
              {translate('ra.action.save')}
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreditPurchaseDialog;
